<template>
  <div
    v-if="currentPills.length"
    class="board-filter-pills"
  >
    <Pill
      v-for="(pill, index) in currentPills"
      :key="`${index}-${pill}`"
      :type="pill"
    />
    <AddFilter :no-button-label="true" />
  </div>
</template>

<script>
import Pill from './FilterPill.vue';
import AddFilter from './AddFilter';
import viewFiltersMixin from '@/mixins/viewFiltersMixin';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { useMyWorkMemberRoute } from '@/utilities/composables/useMyWorkMemberRoute';

export default {
  name: 'FilterPills',

  components: {
    AddFilter,
    Pill,
  },

  mixins: [viewFiltersMixin],

  setup() {
    const { isMyWorkOrMemberRoute, isMyWorkAssignedTab, isMyWorkCreatedTab } =
      useMyWorkMemberRoute();
    const { embeddedBoardAttributes, embed } = useBoardEmbed();

    return {
      isMyWorkOrMemberRoute,
      isMyWorkAssignedTab,
      isMyWorkCreatedTab,
      embeddedBoardAttributes,
      embed,
    };
  },

  computed: {
    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.boardId;
      }
      return this.$store.getters.currentBoardId;
    },

    currentFilters() {
      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        return this.$store.getters.getViewFilters;
      }

      return this.$store.getters.boardFilters(this.currentBoardId) || {};
    },

    currentPills() {
      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        return this.$store.getters.getViewPills;
      }

      return this.$store.getters.boardsPills(this.currentBoardId);
    },

    hasFilterKeys() {
      return this.currentFilters && Object.keys(this.currentFilters || {}).length > 0;
    },

    hasInclude() {
      return this.hasFilterKeys && Object.keys(this.currentFilters?.include || {}).length > 0;
    },

    hasExclude() {
      return this.hasFilterKeys && Object.keys(this.currentFilters.exclude || {}).length > 0;
    },

    hasMembersFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.members &&
          this.currentFilters.include.members.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.members &&
          this.currentFilters.exclude.members.length > 0) ||
        this.currentFilters.has_members !== undefined
      );
    },

    hasCreatorsFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.creators &&
          this.currentFilters.include.creators.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.creators &&
          this.currentFilters.exclude.creators.length > 0)
      );
    },

    hasOwnersFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.owners &&
          this.currentFilters.include.owners.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.owners &&
          this.currentFilters.exclude.owners.length > 0)
      );
    },

    hasTagsFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.tags &&
          this.currentFilters.include.tags.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.tags &&
          this.currentFilters.exclude.tags.length > 0)
      );
    },

    hasStatusesFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.statuses &&
          this.currentFilters.include.statuses.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.statuses &&
          this.currentFilters.exclude.statuses.length > 0) ||
        this.currentFilters.has_status !== undefined
      );
    },

    hasBoardsFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.boards &&
          this.currentFilters.include.boards.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.boards &&
          this.currentFilters.exclude.boards.length > 0)
      );
    },

    hasProjectsFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.projects &&
          this.currentFilters.include.projects.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.projects &&
          this.currentFilters.exclude.projects.length > 0)
      );
    },

    hasExteralLinkFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.external_links &&
          this.currentFilters.include.external_links.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.external_links &&
          this.currentFilters.exclude.external_links.length > 0)
      );
    },

    hasParentFilter() {
      return this.currentFilters.is_parent !== undefined;
    },

    hasLinkedCardsFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.linked_cards &&
          this.currentFilters.include.linked_cards.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.linked_cards &&
          this.currentFilters.exclude.linked_cards.length > 0)
      );
    },

    hasChildrenFilter() {
      return this.currentFilters.is_child !== undefined;
    },

    hasArchivedFilter() {
      return this.currentFilters.is_archived !== undefined;
    },

    hasDueDateFilter() {
      return (
        this.currentFilters.due_date?.before ||
        this.currentFilters.due_date?.after ||
        this.currentFilters.has_due_date !== undefined
      );
    },

    hasCreatedFilter() {
      return this.currentFilters.time_created?.before || this.currentFilters.time_created?.after;
    },

    hasActivityFilter() {
      return this.currentFilters.time_updated?.before || this.currentFilters.time_updated?.after;
    },
    hasPriorityFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.priority &&
          this.currentFilters.include.priority.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.priority &&
          this.currentFilters.exclude.priority.length > 0)
      );
    },
    hasEstimateFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.estimates &&
          this.currentFilters.include.estimates.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.estimates &&
          this.currentFilters.exclude.estimates.length > 0)
      );
    },

    hasParentCardFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.parent_cards &&
          this.currentFilters.include.parent_cards.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.parent_cards &&
          this.currentFilters.exclude.parent_cards.length > 0)
      );
    },

    hasEpicFilter() {
      return (
        (this.hasInclude &&
          this.currentFilters.include.epics &&
          this.currentFilters.include.epics.length > 0) ||
        (this.hasExclude &&
          this.currentFilters.exclude.epics &&
          this.currentFilters.exclude.epics.length > 0)
      );
    },

    hasCardTitleFilter() {
      return this.currentFilters.include?.title || this.currentFilters.exclude?.title;
    },
  },

  watch: {
    hasMembersFilter(newValue) {
      if (newValue) {
        this.addPill('members');
      } else {
        this.removePill('members');
      }
    },

    hasCreatorsFilter(newValue) {
      if (newValue) {
        this.addPill('creators');
      } else {
        this.removePill('creators');
      }
    },

    hasOwnersFilter(newValue) {
      if (newValue) {
        this.addPill('owners');
      } else {
        this.removePill('owners');
      }
    },
    hasNoAssigneeFilter(newValue) {
      if (newValue) {
        this.addPill('members');
      } else {
        this.removePill('members');
      }
    },
    hasTagsFilter(newValue) {
      if (newValue) {
        this.addPill('tags');
      } else {
        this.removePill('tags');
      }
    },
    hasStatusesFilter(newValue) {
      if (newValue) {
        this.addPill('statuses');
      } else {
        this.removePill('statuses');
      }
    },
    hasBoardsFilter(newValue) {
      if (newValue) {
        this.addPill('boards');
      } else {
        this.removePill('boards');
      }
    },
    hasProjectsFilter(newValue) {
      if (newValue) {
        this.addPill('projects');
      } else {
        this.removePill('projects');
      }
    },
    hasParentFilter(newValue) {
      if (newValue) {
        this.addPill('is_parent');
      } else {
        this.removePill('is_parent');
      }
    },
    hasLinkedCardsFilter(newValue) {
      if (newValue) {
        this.addPill('linked_cards');
      } else {
        this.removePill('linked_cards');
      }
    },
    hasChildrenFilter(newValue) {
      if (newValue) {
        this.addPill('is_child');
      } else {
        this.removePill('is_child');
      }
    },
    hasArchivedFilter(newValue) {
      if (newValue) {
        this.addPill('is_archived');
      } else {
        this.removePill('is_archived');
      }
    },
    hasDueDateFilter(newValue) {
      if (newValue) {
        this.addPill('due_date');
      } else {
        this.removePill('due_date');
      }
    },
    hasCreatedFilter(newValue) {
      if (newValue) {
        this.addPill('time_created');
      } else {
        this.removePill('time_created');
      }
    },
    hasActivityFilter(newValue) {
      if (newValue) {
        this.addPill('time_updated');
      } else {
        this.removePill('time_updated');
      }
    },
    hasExteralLinkFilter(newValue) {
      if (newValue) {
        this.addPill('external_links');
      } else {
        this.removePill('external_links');
      }
    },
    hasPriorityFilter(newValue) {
      if (newValue) {
        this.addPill('priority');
      } else {
        this.removePill('priority');
      }
    },
    hasEstimateFilter(newValue) {
      if (newValue) {
        this.addPill('estimates');
      } else {
        this.removePill('estimates');
      }
    },
    hasParentCardFilter(newValue) {
      if (newValue) {
        this.addPill('parent_cards');
      } else {
        this.removePill('parent_cards');
      }
    },
    hasEpicFilter(newValue) {
      if (newValue) {
        this.addPill('epics');
      } else {
        this.removePill('epics');
      }
    },
    hasCardTitleFilter(newValue) {
      if (newValue) {
        this.addPill('title');
      } else {
        this.removePill('title');
      }
    },
    isMyWorkAssignedTab(newValue) {
      if (newValue) {
        this.prioritizePill('members');
      }
    },
    isMyWorkCreatedTab(newValue) {
      if (newValue) {
        this.prioritizePill('creators');
      }
    },
  },

  mounted() {
    const filtersPillBindings = [
      { check: this.hasMembersFilter, pill: 'members' },
      { check: this.hasOwnersFilter, pill: 'owners' },
      { check: this.hasCreatorsFilter, pill: 'creators' },
      { check: this.hasTagsFilter, pill: 'tags' },
      { check: this.hasStatusesFilter, pill: 'statuses' },
      { check: this.hasBoardsFilter, pill: 'boards' },
      { check: this.hasProjectsFilter, pill: 'projects' },
      { check: this.hasParentFilter, pill: 'is_parent' },
      { check: this.hasLinkedCardsFilter, pill: 'linked_cards' },
      { check: this.hasChildrenFilter, pill: 'is_child' },
      { check: this.hasArchivedFilter, pill: 'is_archived' },
      { check: this.hasDueDateFilter, pill: 'due_date' },
      { check: this.hasExteralLinkFilter, pill: 'external_links' },
      { check: this.hasCreatedFilter, pill: 'time_created' },
      { check: this.hasActivityFilter, pill: 'time_updated' },
      { check: this.hasPriorityFilter, pill: 'priority' },
      { check: this.hasEstimateFilter, pill: 'estimates' },
      { check: this.hasParentCardFilter, pill: 'parent_cards' },
      { check: this.hasEpicFilter, pill: 'epics' },
      { check: this.hasCardTitleFilter, pill: 'title' },
    ];

    this.$nextTick(() => {
      filtersPillBindings.forEach(({ check, pill }) => {
        if (check) {
          this.addPill(pill);
        }
      });
    });
  },

  methods: {
    addPill(pill) {
      if (this.currentPills.indexOf(pill) === -1) {
        const pills = [...this.currentPills];
        pills.push(pill);

        if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
          this.setViewPills(pills);
          return;
        }

        this.$store.dispatch('setBoardPills', {
          boardId: this.currentBoardId,
          pills,
        });
      }
    },
    removePill(pill) {
      const index = this.currentPills.indexOf(pill);
      if (index !== -1) {
        const pills = [...this.currentPills];
        pills.splice(index, 1);

        if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
          this.setViewPills(pills);
          return;
        }

        this.$store.dispatch('setBoardPills', {
          boardId: this.currentBoardId,
          pills,
        });
      }
    },
    prioritizePill(pillType) {
      const pills = [pillType, ...this.currentPills.filter((pill) => pill !== pillType)];
      this.setViewPills(pills);
    },
  },
};
</script>

<style lang="scss">
$pillsContainerPadding: 24px;

.board-filter-pills {
  display: flex;
  z-index: 4;
  position: relative;
  flex-wrap: wrap;

  .pills-divier {
    height: 1px;
    width: calc(100% - 2 * #{$pillsContainerPadding});
    position: absolute;
    bottom: 0;
    z-index: -1;
    @apply bg-dividers-default;
  }
}

.add-filter-container {
  display: flex;
  align-items: center;
}
</style>
